import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Crown, Info, Zap, Plus } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { toast } from '@/hooks/use-toast'
import { useShop } from '@/context/shop-context'
import { createPlanCheckout } from '@/requests/billing'
import { usePostCheckoutPolling } from '@/hooks/use-post-checkout-polling'
import { useShopCreationQuota, type SuggestedUpgrade } from '@/hooks/use-shop-creation-quota'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { EnterpriseContactModal } from '@/features/upgrade-account/components/enterprise-contact-modal'
import { ActivatingSpinner } from '@/features/upgrade-account/components/activating-spinner'

const PENDING_SHOP_CREATE_KEY = 'mp_pending_shop_create'

export function setPendingShopCreate(): void {
  sessionStorage.setItem(PENDING_SHOP_CREATE_KEY, '1')
}

export function clearPendingShopCreate(): void {
  sessionStorage.removeItem(PENDING_SHOP_CREATE_KEY)
}

export function hasPendingShopCreate(): boolean {
  return sessionStorage.getItem(PENDING_SHOP_CREATE_KEY) === '1'
}

function resolveShopId(shop: { _id?: string; id?: string } | null): string {
  if (!shop) return ''
  return shop._id ?? (shop as { id?: string }).id ?? ''
}

interface ShopCreationUpgradePanelProps {
  onCancel: () => void
}

export function ShopCreationUpgradePanel({ onCancel }: ShopCreationUpgradePanelProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeShop } = useShop()
  const shopId = resolveShopId(activeShop)
  const { plan, shop_quota, suggestedUpgrade, refetch } = useShopCreationQuota()
  const { isPolling, activated } = usePostCheckoutPolling()
  const [checkingOut, setCheckingOut] = useState<SuggestedUpgrade>(null)
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false)

  if (isPolling) {
    return <ActivatingSpinner />
  }

  if (activated) {
    clearPendingShopCreate()
    void refetch()
  }

  const handleUpgrade = async (target: 'PLUS' | 'PRO') => {
    if (!shopId) {
      toast({
        title: t('Error'),
        description: t('Select a shop first'),
        variant: 'destructive',
      })
      return
    }
    try {
      setCheckingOut(target)
      setPendingShopCreate()
      const result = await createPlanCheckout(shopId, target)
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
      } else if (result.variantChanged) {
        clearPendingShopCreate()
        void refetch()
        toast({ title: t('Success'), description: t('Plan changed') })
      }
    } catch {
      clearPendingShopCreate()
      toast({
        title: t('Error'),
        description: t('Something went wrong'),
        variant: 'destructive',
      })
    } finally {
      setCheckingOut(null)
    }
  }

  const upgradeLabel =
    suggestedUpgrade === 'PRO'
      ? t('Upgrade to Pro')
      : t('Upgrade to Plus')

  const UpgradeIcon = suggestedUpgrade === 'PRO' ? Zap : Plus

  return (
    <div className='space-y-6 mt-8'>
      <Alert className='border-primary/50 bg-primary/5'>
        <Crown className='h-5 w-5 text-primary' />
        <AlertTitle className='text-lg font-semibold mb-2'>
          {t('Upgrade Required')}
        </AlertTitle>
        <AlertDescription className='text-base'>
          {t('You have reached your shop limit.')}{' '}
          {shop_quota.max_shops != null
            ? `${shop_quota.used}/${shop_quota.max_shops} ${t('shops used')}.`
            : null}{' '}
          {t('Upgrade to create more shops.')}
        </AlertDescription>
      </Alert>

      <div className='flex flex-col gap-4 p-6 border rounded-lg bg-muted/30'>
        <div className='flex items-start gap-3'>
          <Info className='h-5 w-5 text-muted-foreground mt-0.5' />
          <div className='space-y-2'>
            <h4 className='font-semibold'>
              {t('Current Plan')}: {plan}
            </h4>
            <p className='text-sm text-muted-foreground'>
              {suggestedUpgrade === 'PRO'
                ? t('Pro includes unlimited shops and advanced features.')
                : t('Plus includes up to 2 shops and unlimited menu items.')}
            </p>
          </div>
        </div>
      </div>

      <div className='flex flex-wrap justify-end gap-3 pt-6 border-t'>
        <Button type='button' variant='outline' onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          type='button'
          variant='outline'
          onClick={() => navigate({ to: '/upgrade-account' })}
        >
          {t('Compare all plans')}
        </Button>
        {suggestedUpgrade && (
          <Button
            type='button'
            onClick={() => void handleUpgrade(suggestedUpgrade)}
            disabled={checkingOut !== null}
            data-testid={`upgrade-${suggestedUpgrade.toLowerCase()}-from-drawer`}
          >
            <UpgradeIcon className='h-4 w-4 mr-2' />
            {checkingOut === suggestedUpgrade ? t('Redirecting…') : upgradeLabel}
          </Button>
        )}
        <Button
          type='button'
          variant='secondary'
          onClick={() => setEnterpriseModalOpen(true)}
        >
          {t('Enterprise')}
        </Button>
      </div>

      <EnterpriseContactModal
        open={enterpriseModalOpen}
        onOpenChange={setEnterpriseModalOpen}
      />
    </div>
  )
}
